<template>
  <ReportLayout topbar-color="white" @period-changed="onPeriodChanged" :back-page="isMultiCenter ? { name: 'Dashboard' } : { name: 'Center', params: { centerId } }">
    <div class="grid gap-5">
      <SalesSummaryCard
        :card="card"
        @click="router.push({
          name: 'CenterReport',
          params: {
            centerId: centerId.toString(),
            id: srId
          }
        })" />

      <FamiliesSummaryCard
        :products-report="card?.productsReport"
        @click="router.push({
          name: 'CenterReportFamilies',
          params: {
            centerId: centerId.toString(),
            id: srId
          }
        })" />

      <ClientTypesSummaryCard
        :client-types-report="card?.clientTypesReport"
        :loading="clientTypesLoading"
        :tax-key="taxKey"
        :center="centerObj"
        @click="router.push({
          name: 'CenterClientTypes',
          params: {
            centerId: centerId.toString(),
            id: srId
          }
        })" />

      <ClientsSummaryCard
        :clients-report="card?.clientsReport"
        :loading="clientsLoading"
        @click="router.push({
          name: 'CenterClients',
          params: {
            centerId: centerId.toString(),
            id: srId
          }
        })" />

      <MembersSummaryCard
        :members-report="card?.membersReport"
        :members-conversion-data="membersConversionData"
        :loading="membersLoading"
        @click="router.push({
          name: 'CenterMembers',
          params: { centerId: centerId.toString(), id: srId }
        })" />

    </div>
  </ReportLayout>

</template>
<script setup lang="ts">
import { onMounted, computed, ref } from 'vue';
import { useRouter } from 'vue-router'
import { useSalesReportsStore } from '../../stores/salesReports'
import { useUserManagerStore } from '../../stores/userManagerStore'
import { TaxDataKey } from '../../responses'
import ReportLayout from '../../layouts/ReportLayout.vue'
import SalesSummaryCard from '../../components/report-sections/SalesSummaryCard.vue'
import FamiliesSummaryCard from '../../components/report-sections/FamiliesSummaryCard.vue'
import ClientTypesSummaryCard from '../../components/report-sections/ClientTypesSummaryCard.vue'
import ClientsSummaryCard from '../../components/report-sections/ClientsSummaryCard.vue'
import MembersSummaryCard from '../../components/report-sections/MembersSummaryCard.vue'
import type { SalesReport } from '../../models/SalesReport'
import { getSmartTimeAggregation } from '../../types/customCharts';
import { MembersApi } from '../../api/MembersApi';
import type { MembersStatsResponse } from '../../responses';
const router = useRouter();

const salesReportsStore = useSalesReportsStore();
const userManagerStore = useUserManagerStore();

const clientTypesLoading = ref(true)
const clientsLoading = ref(true)
const membersLoading = ref(true)

const centerId = parseInt(router.currentRoute.value.params.centerId as string);
const isMultiCenter = computed(() => userManagerStore.displayedCenters.length > 1);
const srId = router.currentRoute.value.params.id as string;

const card = computed(() => {
  return salesReportsStore.getReportForCenterAndComponent(centerId, srId) as SalesReport | undefined;
})

const centerObj = computed(() => {
  return userManagerStore.displayedCenters.find(c => c.id === centerId)
})

const taxKey = computed<TaxDataKey>(() => {
  return card.value?.taxIncluded ? 'included_tax' : 'excluded_tax'
})

const membersConversionData = ref<MembersStatsResponse | null>(null)

const onPeriodChanged = async () => {
  if (!card.value) return
  card.value.data = null
  card.value.productsReport = null
  card.value.clientTypesReport = null
  card.value.clientsReport = null
  card.value.membersReport = null
  card.value.membersConversionData = null
  membersConversionData.value = null
  clientTypesLoading.value = true
  clientsLoading.value = true
  membersLoading.value = true

  const smartAgg = getSmartTimeAggregation(card.value.date.startDate.toJSDate(), card.value.date.endDate.toJSDate())

  try {
    await Promise.all([
      card.value.fetchData(centerId, true),
      card.value.fetchProductsReport(),
      card.value.fetchClientTypesReport(smartAgg),
      card.value.fetchClientsReport(smartAgg),
      card.value.fetchMembersReport(['recency'], ['count'], smartAgg),
      fetchMembersExtraData(smartAgg),
    ])
  } catch {
    // errors handled inside fetch methods
  } finally {
    clientTypesLoading.value = false
    clientsLoading.value = false
    membersLoading.value = false
  }
}

async function fetchMembersExtraData(smartAgg?: string) {
  if (!card.value) return
  // Use cached data from SalesReport model if available
  if (card.value.membersConversionData) {
    membersConversionData.value = card.value.membersConversionData
    return
  }
  const interval = (smartAgg ?? card.value.interval) as any
  const resp = await MembersApi.fetchMembersStats(
    card.value.date.startDate, card.value.date.endDate,
    interval, centerId, ['first_activity'], ['count'],
    card.value.previousDate?.startDate, card.value.previousDate?.endDate
  )
  membersConversionData.value = resp
  card.value.membersConversionData = resp
}

onMounted(async () => {
  if (card.value) {
    card.value.centerId = centerId;
  }

  if (card.value?.productsReport == null) {
    card.value?.fetchProductsReport();
  }

  if (card.value?.data == null) {
    card.value?.fetchData(centerId, true);
  }

  const smartAgg = card.value ? getSmartTimeAggregation(card.value.date.startDate.toJSDate(), card.value.date.endDate.toJSDate()) : undefined;

  // Fetch client types data (skip if already cached on the model)
  if (card.value?.clientTypesReport == null) {
    try {
      await card.value?.fetchClientTypesReport(smartAgg)
    } catch {
      // Silently fail - card will show no data
    } finally {
      clientTypesLoading.value = false
    }
  } else {
    clientTypesLoading.value = false
  }

  // Fetch clients data (skip if already cached on the model)
  if (card.value?.clientsReport == null) {
    try {
      await card.value?.fetchClientsReport(smartAgg)
    } catch {
      // Silently fail - card will show no data
    } finally {
      clientsLoading.value = false
    }
  } else {
    clientsLoading.value = false
  }

  // Fetch members data (skip if already cached)
  if (card.value?.membersReport == null) {
    try {
      await Promise.all([
        card.value?.fetchMembersReport(['recency'], ['count'], smartAgg),
        fetchMembersExtraData(smartAgg),
      ])
    } catch {
      // Silently fail - card will show no data
    } finally {
      membersLoading.value = false
    }
  } else {
    // Still fetch extra data if not cached locally
    if (!membersConversionData.value) {
      fetchMembersExtraData(smartAgg).catch(() => {})
    }
    membersLoading.value = false
  }

})
</script>
